/* eslint-disable no-unused-vars */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ModalShell from "./ModalShell";

export default function LeadGateModal({
  open,
  onClose,
  onSuccess,
  calculatorName,
  calculatorYear,
  pageUrl,
  endpoint,
  resultsJson,
  inputsJson,
  pdf_notes,
}) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [agree, setAgree] = useState(false);

  const UNK_GATE_KEY = "unk_calc_unlock_v1";
  const UNK_GATE_TTL_MS = 24 * 60 * 60 * 1000;

      const params = useMemo(() => new URLSearchParams(window.location.search), []);
    
      const qa = useMemo(
      () => (params.has("qa_mode")),
      [params]
    );

  const writeUnlock = ({ email }) => {
    const payload = {
      expiresAt: Date.now() + UNK_GATE_TTL_MS,
      email: String(email || ""),
    };
    localStorage.setItem(UNK_GATE_KEY, JSON.stringify(payload));
  };

  useEffect(() => {
    if (!open) return;
    setErr("");
  }, [open]);

  const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

  // proxy request helper (same as you already had)
  const proxyPost = async (path, data) => {
    const resp = await axios.post(
      endpoint,
      { path, method: "POST", raw: true, data, qa_mode: qa },
      { headers: { "Content-Type": "application/json" }, timeout: 30000 }
    );

    // ❗ Your proxy seems to put errors either as success:false OR throws 4xx.
    // Keep success:false handling:
    if (resp?.data?.success === false) {
      throw new Error(resp?.data?.message || "Request failed");
    }

    return resp?.data;
  };

  // 1) Create user in GHL (backend route)
  const createUserGhl = async ({ first_name, last_name, email }) => {
    // change path to your real backend public route
    return proxyPost("/public/create-contact-ghl", {
      firstName: first_name,
      lastName: last_name,
      email,
      tags: ["calculator_contact"],
      agree_to_receive_emails: agree,
    });
  };


  // 2) Send results email (backend route)
  const sendResultsEmail = async (basePayload) => {
    return proxyPost("/public/send-results", {
      ...basePayload,
      action: "send_results_email",
      pdf_notes: pdf_notes || {},
    });
  };

  const handleSubmit = async () => {
    setErr("");

    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim().toLowerCase();

    if (!fn) return setErr("First Name is required.");
    if (!ln) return setErr("Last Name is required.");
    if (!em) return setErr("Email Address is required.");
    if (!isValidEmail(em)) return setErr("Please enter a valid email address.");

    setSubmitting(true);

    const basePayload = {
      firstName: fn,
      lastName: ln,
      tags: ["calculator_contact"],
      email: em,
      calculator_name: calculatorName,
      calculator_year: calculatorYear,
      page_url: pageUrl,
      timestamp_utc: new Date().toISOString(),
      results_json: resultsJson,
      inputs_json: inputsJson,
      schema_version: 1,
    };

    try {
      // --- Step 1: create user (unless already exists) ---
      let userCreatedOrAlreadyExists = false;

      try {
        await createUserGhl({
          first_name: fn,
          last_name: ln,
          email: em,
        });
        userCreatedOrAlreadyExists = true;
      } catch (e) {
        // If user already exists → backend returns 403 with message.
        // We want to continue and still send email.
        const msg = String(e?.message || "");
        const looksLikeAlreadyExists =
          msg.toLowerCase().includes("already exists") ||
          msg.toLowerCase().includes("exists");

        if (looksLikeAlreadyExists) {
          userCreatedOrAlreadyExists = true;
        } else {
          // real failure: stop here
          throw e;
        }
      }

      // --- Step 2: send results email ---
      await sendResultsEmail(basePayload);

      // --- Only after BOTH conditions are ok ---
      // unlock + close modals
      writeUnlock({ email: em });
      onSuccess?.({ email: em }); // parent can close modal(s) only here
    } catch (e) {
      console.error("LeadGateModal submit error:", e);
      setErr(e?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  
  useEffect(() => {
  if (!open) return;
  setErr("");
  setAgree(false);
}, [open]);


  return (
    <ModalShell open={open} onClose={onClose} width={520}>
      {/* ... UI unchanged ... */}
      <div
        className="p-4"
        style={{
          background: "linear-gradient(135deg, #0b1f3a 0%, #103a6b 100%)",
          color: "white",
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          className="btn btn-sm btn-light"
          style={{
            position: "absolute",
            right: 14,
            top: 24,
            borderRadius: 10,
          }}
          aria-label="Close"
          disabled={submitting} // optional: prevent closing mid-submit
        >
          ✕
        </button>

        <div className="d-flex flex-column p-3 align-items-start gap-3">
      <div
  style={{
    width: 64,
    height: 64,
    borderRadius: 16,
    background: "radial-gradient(circle at top, #1f4f45, #0f2f2a)",
    boxShadow:
      "inset 0 0 0 1px rgba(79, 255, 210, 0.15), 0 0 20px rgba(79, 255, 210, 0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  }}
>
<svg
  width="28"
  height="28"
  viewBox="0 0 24 24"
  fill="none"
  stroke="#4fffd2"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
</svg>

</div>


          <div>
            <div className="fw-bold" style={{ fontSize: 22, lineHeight: 1.2 }}>
             Unlock Your Personalized Tax Results
            </div>
            <div style={{ opacity: 0.9, marginTop: 6 }}>
             Get instant access to your calculated results—plus insights that help you plan smarter before you file.
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-white">
        <div className="border rounded-3 p-3 mb-4" style={{ background: "#f7fbff" }}>
          <div className="d-flex align-items-start gap-2 mb-2">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
     style={{width:24, height:24, color:"#198754", fontSize:13, fontWeight:700}}>
  ✓
</div>

            <span>Estimated tax impact based on your inputs</span>
          </div>
          <div className="d-flex align-items-start gap-2 mb-2">
                        <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
     style={{width:24, height:24, color:"#198754", fontSize:13, fontWeight:700}}>
  ✓
</div>
            <span>Clear breakdown of how the numbers are calculated</span>
          </div>
          <div className="d-flex align-items-start gap-2">
                   <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10"
     style={{width:24, height:24, color:"#198754", fontSize:13, fontWeight:700}}>
  ✓
</div>
            <span>Actionable insights to help you keep more of your money</span>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">First Name</label>
          <input
            className="form-control"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Last Name</label>
          <input
            className="form-control"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-semibold">Email Address</label>
          <input
            className="form-control"
            placeholder="john.doe@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {err && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {err}
          </div>
        )}
<div
  className="d-flex align-items-start gap-2 mb-3"
  style={{
    padding: "12px 14px",
    borderRadius: 12,
    background: "#f1fbf6",
    border: "1px solid #ccefe0",
  }}
>
  <input
    type="checkbox"
    id="agreeCheckbox"
    checked={agree}
    onChange={(e) => setAgree(e.target.checked)}
    disabled={submitting}
    style={{
      width: 18,
      height: 18,
      marginTop: 3,
      accentColor: "#0f8a4c", 
      cursor: "pointer",
    }}
  />

  <label
    htmlFor="agreeCheckbox"
    style={{
      cursor: "pointer",
      fontSize: 14,
      lineHeight: 1.4,
    }}
  >
    Send me the 2026 Tax Savings Guide with strategies to legally reduce my taxes this year.
  </label>
</div>


        <button
          type="button"
          className="btn w-100 fw-semibold"
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            background: "#0f8a4c",
            borderColor: "#0f8a4c",
            color: "white",
            padding: "12px 14px",
            borderRadius: 12,
          }}
        >
          {submitting ? "Submitting..." : "Submit"}
        </button>

        <div className="mt-3">
          <small className="text-muted">
           Your information is secure and will never be sold. We use it only to deliver your results and insights. By continuing, you agree to our Privacy Policy.
          </small>
        </div>
      </div>
    </ModalShell>
  );
}
